import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchLocationList, selectLocationList } from "../../../Redux/Slices/LocationListSlice";

import TimesheetService, { TIMESHEET_CONSTANTS } from "../TimesheetService";
import {
    DKLabel,
    DKButton,
    DKInput,
    DKCheckMark,
    DKTooltipWrapper,
    DKIcon,
    DKIcons,
    DKSpinner,
    Toggle,
    INPUT_TYPE,
    INPUT_VIEW_DIRECTION,
    showLoader,
    removeLoader,
    showToast,
    TOAST_TYPE,
    showAlert,
} from "deskera-ui-library";
import TimesheetUtils from "../TimesheetUtils";
import { selectBooksContact } from "../../../Redux/Slices/BooksContactSlice";
import { selectProject } from "../../../Redux/Slices/ProjectSlice";
import moment from "moment";
import DKInputDateTime from "../../common/DKInputDateTime";
import { STATUS_OPTION } from "../../../constants/Constant";
import { selectTask } from "../../../Redux/Slices/TaskSlice";
import { selectEmployeeListWithoutLimit } from "../../../Redux/Slices/EmployeeListSlice";
import { TimesheetReportService } from "./TimesheetReportService";
import { ENTRY_STATUS } from "../TimesheetConstants";
import Utility from "../../../utility/Utility";

function AddTimesheetReport(props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const projectList = useSelector(selectProject);
    const locations = useSelector(selectLocationList);
    const booksContact = useSelector(selectBooksContact);
    const employeeResponse = useSelector(selectEmployeeListWithoutLimit);
    const task = useSelector(selectTask);
    const initialFormState = {
        jobId: {
            id: "jobId",
            title: t("Task"),
            type: INPUT_TYPE.DROPDOWN,
            validation: [["required"]],
            options: task?.data?.map(e=>{ return {...e,label:e.name,value: e.id}}) || [],
        },
        customer: {
            id: "customer",
            title: t("Customer"),
            type: INPUT_TYPE.DROPDOWN,  
            readOnly:true,      
            tooltip:"You must have books access to fetch customer",
            options: booksContact?.map(e=>{ return {...e,label:e.name,value: e.id?.toString()}}) || []
        },
        projectId: {
            id: "projectId",
            title: t("Project"),
            type: INPUT_TYPE.DROPDOWN,
            options: projectList?.data?.map(e=>{ return {...e,label:e.name,value: e.id}}) || [],
            readOnly: true
        },
        date: {
            id: "date",
            title: t("Date"),
            type: INPUT_TYPE.DATE,
            validation: [["required"]],
            value: new Date()
        },
        fromDate: {
            id: "fromDate",
            title: t("From"),
            type: "date-time-input",
            validation: [["required"]],
            is12Hour: true,
            hideFooterDateTime: false,
            value: new Date()
        },
        toDate: {
            id: "toDate",
            title: t("To"),
            type: "date-time-input",
            validation: [["required"]],
            is12Hour: true,
            hideFooterDateTime: false,
            value: new Date()
        },
        employeeId: {
            id: "employeeId",
            title: t("Employee"),
            type: INPUT_TYPE.DROPDOWN,
            validation: [["required"]],
            options: employeeResponse?.users?.map(e=>{ return {...e,label:e.name,value: e.userId?.toString()}}) || []
        },
        isBillable: {
            id: "isBillable",
            title: t("Billable"),
            type: "toggle",
            value: false
        }    
    }

    const [formState, setFormState] = useState(initialFormState);
    const [report, setReport] = useState(null);
    const [fetchingDependencies, setFetchingDependencies] = useState(true);

    useEffect(() => {
        if (TimesheetService.REPORT) {
            setReport(TimesheetService.REPORT);
            TimesheetService.REPORT = null;
        }

        fetchDependencies();
    }, []);

    useEffect(() => {
        if (!fetchingDependencies) {
            const newState = { ...formState };

            if (TimesheetUtils.isTrackLocation()) {
                TimesheetUtils.enrichWithLocation(newState, locations);
            }

            if (report) {
                setReportData(newState);
            }

            setFormState(newState);
        }
    }, [fetchingDependencies, report, locations]);

    const fetchDependencies = async () => {
        if (TimesheetUtils.isTrackLocation()) {
            await dispatch(fetchLocationList());
            setFetchingDependencies(false);
        } else {
            setFetchingDependencies(false);
        }
    }

    const setReportData = (newState) => {
        let projectName = getDropdownLabel(report.projectId, newState.projectId.options)
        let employeeName = getDropdownLabel(report.employeeId, newState.employeeId.options)
        newState.customer.value = getDropdownLabel(report.customer, newState.customer.options);
        newState.projectId.value = projectName === report.projectId ? report.projectName: projectName;
        newState.jobId.value = getDropdownLabel(report.jobId, newState.jobId.options);
        newState.employeeId.value = employeeName === report.employeeId ? report.employeeName: employeeName;
        newState.isBillable.value = report.isBillable;     
        newState.fromDate.value = new Date(report.fromDate);
        newState.date.value = new Date(report.fromDate);
        newState.toDate.value = new Date(report.toDate);
        newState['employeeId'].readOnly = true;
    }

    const getDropdownLabel = (v, o) => {
        let value = v;

        const index = o.findIndex(i => i.value === v);
        if (index !== -1) {
            value = o[index].label;
        }

        return value;
    }

    const getDropdownValue = (v, o) => {
        let value = v;

        const index = o.findIndex(i => i.label === v);
        if (index !== -1) {
            value = o[index].value;
        }

        return value;
    }

    const isRequired = (validation) => {
        return validation ? validation.findIndex((v) => v[0] === "required") !== -1 : false;
    }

    const hasError = (field, value) => {
        let error = {
            invalid: false,
            errorMessage: null
        };

        const validation = field.validation;

        if (validation && validation.length !== 0) {
            validation.forEach((validation) => {
                switch (validation[0]) {
                    case "required":
                        if (value === null || value === undefined || String(value).trim() === "") {
                            error.invalid = true;
                        }
                        break;
                    default:
                        error.invalid = false;
                        error.errorMessage = null;
                        break;
                }
            });
        }
        switch(field.id) {
            case "fromDate":
            case "toDate":
                if(field.invalid) {
                    error.invalid = true;
                    error.errorMessage = 'From time should not be less than To Time';
                } else {
                    error.invalid = false;
                    error.errorMessage = null;
                    break;
                }
                break;
            default :
                break;
        }

        return error;
    }

    const updateState = (stateName, field, value) => {
        const newState = { ...formState };
        newState[stateName] = { ...field, value: value, ...hasError(field, value) };
        updateValueAndValidity(stateName, newState);

        setFormState(newState);
    }

    const updateValueAndValidity = (stateName, state) => {
        switch (stateName) {
            case "task":
            case "jobId":

                const taskId = getDropdownValue(state.jobId.value, state.jobId.options);
                if (taskId) {
                    const taskData = TimesheetUtils.findTask(task?.data, taskId);
                    
                    if (taskData) {
                        if(!Utility.isEmpty(taskData.projectId)) {
                            let projectData = TimesheetUtils.findProject(projectList?.data, taskData.projectId)
                            if(projectData) {
                                console.log(booksContact)
                                state.projectId.value = projectData.name;
                                state.customer.value = booksContact?.find(c=>c.id == projectData.customer)?.name || '';
                            }
                        }
                    } else {
                        state.projectId.value = taskId.projectName;
                        state.customer.value = taskId.customerName;
                        state.isBillable.value = taskId.isBillable;
                    }
                }
                break;
            case "customer":
                const customerId = getDropdownValue(state.customer.value, state.customer.options);
                const projectOption = projectList?.data?.map(e=>{ return {...e,label:e.name,value: e.id}})
                state.projectId.options = projectOption?.filter((p) => p.customer === customerId).map((p) => {
                        return {
                            value: p.id,
                            label: p.name
                        }
                    });
                state.projectId.readOnly = false;
                state.projectId.value = null;
                break;
                case "fromDate":
                    case "toDate":
                        const from = state.fromDate.value;
                        const to = state.toDate.value;
                        if(moment(from).isAfter(to,'minute')) {
                            state[stateName] = {...state[stateName],...{
                                invalid: true,
                                errorMessage: 'From time should not be less than To Time'
                            }}
                        } else {
                            state['fromDate'] = {...state['fromDate'],...{
                                invalid: false,
                                errorMessage: ''
                            }}
                            state['toDate'] = {...state['toDate'],...{
                                invalid: false,
                                errorMessage: ''
                            }}
                        }
                        break;
            default:
                break;
        }
    }

    const isInvalid = () => {
        let invalid = false;

        const newState = { ...formState };
        Object.entries(newState).forEach(([key, field]) => {
            const error = hasError(field, field.value);
            field.invalid = error.invalid;
            field.errorMessage = error.errorMessage;

            if (field.invalid) {
                invalid = true;
            }
        });

        setFormState(newState);

        return invalid;
    }

    const getPayload = () => {
        const sTime = new Date(formState.fromDate.value);
        const fromDate = new Date(formState.date.value);
        fromDate.setHours(sTime.getHours());
        fromDate.setMinutes(sTime.getMinutes());

        const eTime = new Date(formState.toDate.value);
        const toDate = new Date(formState.date.value);
        toDate.setHours(eTime.getHours());
        toDate.setMinutes(eTime.getMinutes());

        const payload = {
            customerName: formState.customer.value,
            customer: getDropdownValue(formState.customer.value, formState.customer.options),
            projectName: formState.projectId.value,
            projectId: (report && report.projectName === formState.projectId.value ) ? report.projectId : getDropdownValue(formState.projectId.value, formState.projectId.options),
            jobId: getDropdownValue(formState.jobId.value, formState.jobId.options),
            employeeId: (report && report.employeeName === formState.employeeId.value ) ? report.employeeId : getDropdownValue(formState.employeeId.value, formState.employeeId.options),
            date: moment(formState.date.value).format(),
            fromDate: moment(fromDate).format(),
            toDate: moment(toDate).format(),
            isBillable: formState.isBillable.value,
            status: ENTRY_STATUS.SUBMITTED
        };

        if (TimesheetUtils.isTrackLocation()) {
        // Note: will use this while integration

            // payload["location"] = getDropdownValue(formState.location.value, formState.location.options);
        }

        if (report) {
            payload.id = report.id;
        }

        return payload;
    }
    const onDraft=(data)=>{
        if (report.id) {
            if (report.status === "APPROVED") {
                return;
            }
            let payload = {
                ids: [`${report.id}`]
            };
            TimesheetService.draftTimesheetEntry(payload).then(() => {
                showToast("Timesheets updated successfully and save as a draft.", TOAST_TYPE.SUCCESS);
                removeLoader();
                props.onClose(true);
            }, (error) => {
                showToast("Timesheet updated successfully", TOAST_TYPE.SUCCESS);
                removeLoader();
                props.onClose(true);
            })
        } else {
            showToast("Timesheet updated successfully", TOAST_TYPE.SUCCESS);
            removeLoader();
            props.onClose(true);
        }
    }
    const saveReport = () => {
        if (isInvalid()) {
            return;
        }

        const payload = getPayload();
        showLoader();
       
        if(report?.id) {

             TimesheetService.updateTimesheetEntry(payload).then(res => {
                if(report.status === ENTRY_STATUS.REJECTED) {
                    onDraft(payload)
                } else {
                    showToast("Timesheet updated successfully", TOAST_TYPE.SUCCESS);
                    removeLoader();
                    props.onClose(true);
                }
                 
             }, error => {
                 removeLoader();
                 const message = error && error.message ? error.message : t("SOMETHING_WENT_WRONG")
                 showToast(message, TOAST_TYPE.FAILURE);
             })
        } else {

             TimesheetService.saveTimesheetEntry(payload).then(res => {
                 showToast("Timesheet added successfully", TOAST_TYPE.SUCCESS);
                 removeLoader();
                 props.onClose(true);
             }, error => {
                 removeLoader();
                 const message = error && error.message ? error.message : t("SOMETHING_WENT_WRONG")
                 showToast(message, TOAST_TYPE.FAILURE);
             })
        }
    }

    const renderField = (stateName, field, width, className) => {
        switch (field?.type) {
            case INPUT_TYPE.TEXT:
            case INPUT_TYPE.NUMBER:
            case INPUT_TYPE.DATE:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative pb-l mb-s ${className} `}>
                            <DKInput
                                ref={field.ref}
                                title={field.title}
                                type={field.type}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || fetchingDependencies}
                                required={isRequired(field.validation)}
                                tooltip={{
                                    content: field.tooltip,
                                    className: "popup-tooltip-prop"
                                }}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={(value) => {
                                    updateState(stateName, field, value);
                                }}
                                validator={field.validator}
                            />
                        </div>
                    </div>
                );
            case INPUT_TYPE.DROPDOWN:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className}`}>
                            <DKInput
                                title={field.title}
                                type={INPUT_TYPE.DROPDOWN}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                dropdownConfig={{
                                    allowSearch: field.options?.length > 6,
                                    searchableKey: "label",
                                    data: field.options,
                                    renderer: (i, item) => item.label,
                                    onSelect: (i, value) => {
                                        updateState(stateName, field, value.label);
                                    }
                                }}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || fetchingDependencies}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                tooltip={{
                                    content: field.tooltip,
                                    className: "popup-tooltip-prop"
                                }}
                                onChange={() => { }}
                            />
                        </div>
                    </div>
                );
            case INPUT_TYPE.MULTI_SELECT:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className} ${field.readOnly && "opacity-5"}`}>
                            <DKInput
                                title={field.title}
                                type={INPUT_TYPE.DROPDOWN}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                dropdownConfig={{
                                    multiSelect: true,
                                    checkMarkColor: "bg-app",
                                    allowSearch: field.options?.length > 6,
                                    searchableKey: "label",
                                    data: field.options,
                                    renderer: (i, item) => item.label,
                                    onSelect: (i, value) => { },
                                }}
                                displayKey={"label"}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || fetchingDependencies}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                tooltip={{
                                    content: field.tooltip,
                                    className: "popup-tooltip-prop"
                                }}
                                onChange={(value) => {
                                    updateState(stateName, field, value);
                                }}
                            />
                        </div>
                    </div>
                );
            case INPUT_TYPE.CHECKBOX:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className="row position-relative pb-l mb-s">
                            <div className="d-flex dkinput-header-section mr-l">
                                <DKLabel className="mb-xs" text={`${field.title} ${isRequired(field.validation) ? "*" : ""}`} />
                                {field.tooltip &&
                                    <DKTooltipWrapper content={field.tooltip} tooltipClassName="checkbox-tooltip-prop">
                                        <DKIcon src={DKIcons.ic_info} className="ic-s ml-s cursor-hand" style={{ opacity: 0.4 }} />
                                    </DKTooltipWrapper>
                                }
                            </div>
                            <DKCheckMark
                                isSelected={field.value}
                                color="bg-app"
                                onClick={() => {
                                    updateState(stateName, field, !field.value);
                                }}
                            />
                        </div>
                    </div>
                );
            case "toggle":
                return (
                    <div key={field.id} style={{ flex: "0 0 100%", maxWidth: width }}>
                        <div className="row pb-l mb-s position-relative">
                            <DKLabel style={{ width: "46%" }} className="mb-xs" text={isRequired(field.validation) ? `${field.title} *` : field.title} />
                            <Toggle
                                style={{ width: "20%" }}
                                isOn={field.value}
                                onChange={() => {
                                    updateState(stateName, field, !field.value);
                                }}
                            />
                        </div>
                    </div>
                );
            case "date-time-input":
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className} ${field.readOnly && "opacity-5"}`}>
                            <DKInputDateTime
                                className="parent-width"
                                is12Hour={field.is12Hour}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                title={isRequired(field.validation) ? `${field.title} *` : field.title}
                                hideLabel={!!field.hideLabel}
                                hideCalendar={true}
                                hideFooterDateTime={field.hideFooterDateTime}
                                selectedDate={moment(field.value, "hh:mm A").format()}
                                readOnly={!!field.readOnly}
                                errorMessage={field.errorMessage}
                                invalid={field.invalid}
                                onSave={(date) => {
                                    updateState(stateName, field, new Date(date));
                                }}
                            />
                        </div>
                    </div>
                );
            default:
                break;
        }
    }

    return (
        <div className="transparent-background">
            <div className="popup-window" style={{ padding: 0, overflow: "hidden", maxWidth: 510 }}>
                <div className="parent-width" style={{ height: "65vh" }}>
                    <div className="row p-h-xl pt-xl pb-m">
                        <DKLabel text={report ? t("Edit Timesheet") : t("Add Timesheet")} className="fs-l fw-b" />
                        {fetchingDependencies && <DKSpinner className="pl-s" iconClassName="ic-s-2" />}
                        <div className="ml-auto d-flex">
                            <DKButton
                                title={t("CANCEL")}
                                className="border-radius-m bg-gray1 border-m"
                                onClick={() => {
                                    props.onClose();
                                }}
                            />
                            <DKButton
                                title={t("SAVE")}
                                className={` ml-m ${report && report.status === TIMESHEET_CONSTANTS.APPROVED ? "bg-gray text-white":"bg-app text-white"}`}
                                disabled={report && report.status === TIMESHEET_CONSTANTS.APPROVED}
                                onClick={() => {
                                    if(report && report.status === TIMESHEET_CONSTANTS.APPROVED) {
                                        return
                                    }
                                    saveReport();
                                }}
                            />
                        </div>
                    </div>
                    <div className="parent-width custom-scroll" style={{ overflow: "auto", height: "calc(100% - 69px)" }}>
                        <div className="p-h-xl p-v-xxl">
                        {renderField("jobId", formState.jobId, "100%")}
                        {renderField("projectId", formState.projectId, "100%")} 
                            {renderField("customer", formState.customer, "100%")}
                                                       
                            {renderField("date", formState.date, "100%","date-bottom")}
                            {renderField("fromDate", formState.fromDate, "100%")}
                            {renderField("toDate", formState.toDate, "100%")}
                            {renderField("employeeId", formState.employeeId, "100%")}
                            {renderField("isBillable", formState.isBillable, "100%")}
                            {renderField("ratePerHour", formState.ratePerHour, "100%")}
                            
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddTimesheetReport;
